/**
 * Admin Stuck Refunds Page
 *
 * Dashboard for viewing and resolving bookings stuck in cancellation_pending
 * with failed refunds. Consumes AdminStuckRefundController.ts REST API.
 *
 * API Endpoints:
 *   GET  /api/v1/admin/stuck-refunds
 *   POST /api/v1/admin/stuck-refunds/:bookingId/resolve
 *
 * Story: SCRUM-1201
 * Sprint: MVP 1.5.3 — Cart & Double-Entry Journal Ledger
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiRequest from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Modal from '../../components/shared/Modal';
import {
  AlertCircle,
  RefreshCw,
  CheckCircle,
  Clock,
} from 'lucide-react';

export default function StuckRefunds() {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Resolve modal state
  const [resolveTarget, setResolveTarget] = useState(null);
  const [stripeRefundId, setStripeRefundId] = useState('');
  const [resolveLoading, setResolveLoading] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchStuckRefunds = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest('/api/v1/admin/stuck-refunds');

      if (response.success) {
        setCancellations(response.data.cancellations || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to load stuck refunds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStuckRefunds();
  }, [fetchStuckRefunds]);

  const formatCurrency = (cents) => {
    if (!cents && cents !== 0) { return 'N/A'; }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) { return 'N/A'; }
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const openResolveModal = (item) => {
    setResolveTarget(item);
    setStripeRefundId('');
    setResolveError(null);
  };

  const closeResolveModal = () => {
    setResolveTarget(null);
    setStripeRefundId('');
    setResolveError(null);
  };

  const handleResolve = async () => {
    if (!stripeRefundId.trim()) {
      setResolveError('Stripe Refund ID is required');
      return;
    }

    try {
      setResolveLoading(true);
      setResolveError(null);

      const response = await apiRequest(
        `/api/v1/admin/stuck-refunds/${resolveTarget.bookingId}/resolve`,
        {
          method: 'POST',
          body: JSON.stringify({ stripeRefundId: stripeRefundId.trim() }),
        }
      );

      if (response.success) {
        setCancellations((prev) =>
          prev.filter((c) => c.bookingId !== resolveTarget.bookingId)
        );
        closeResolveModal();
        setSuccessMessage(`Refund resolved for booking ${resolveTarget.bookingId.slice(0, 8)}...`);
        setTimeout(() => setSuccessMessage(null), 4000);
      } else {
        setResolveError(response.error || 'Failed to resolve refund');
      }
    } catch (err) {
      setResolveError(err.message || 'Failed to resolve refund');
    } finally {
      setResolveLoading(false);
    }
  };

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="stuck-refunds"
      onNavigate={(id, path) => path && navigate(path)}
      user={{ name: authUser?.fullName || authUser?.firstName || 'Admin', email: authUser?.email || '', avatar: authUser?.profilePicture || null }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stuck Refunds</h1>
            <p className="text-sm text-gray-500 mt-1">
              Bookings stuck in cancellation_pending with failed refunds
            </p>
          </div>
          <button
            onClick={fetchStuckRefunds}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition text-sm font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Success message */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-sm text-green-700">{successMessage}</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
            <button
              onClick={fetchStuckRefunds}
              className="text-sm text-red-600 hover:text-red-800 font-medium"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
              <span className="ml-3 text-gray-600">Loading stuck refunds...</span>
            </div>
          ) : cancellations.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-gray-500">No stuck refunds — all cancellations are resolving normally</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Booking ID</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Guide</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">Customer</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Experience</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Refund Amount</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Days Pending</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Retry Count</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 hidden lg:table-cell">Guide Notified</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {cancellations.map((item) => (
                    <tr key={item.bookingId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-600" title={item.bookingId}>
                          {(item.bookingId || '').slice(0, 8)}...
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-gray-900">{item.guideName || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="text-gray-900">{item.customerName || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-700">{item.experienceName || 'N/A'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-green-600">
                          {formatCurrency(item.refundAmountCents)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                          item.daysPending >= 7 ? 'text-red-600' : item.daysPending >= 3 ? 'text-yellow-600' : 'text-gray-600'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {item.daysPending}d
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-600">{item.retryCount || 0}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs hidden lg:table-cell">
                        {formatDate(item.guideNotifiedAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => openResolveModal(item)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-primary text-white rounded-md hover:bg-brand-dark transition text-xs font-medium"
                        >
                          Trigger Refund
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Resolve Modal */}
        <Modal
          isOpen={!!resolveTarget}
          onClose={closeResolveModal}
          title="Trigger Manual Refund"
          footer={
            <>
              <button
                onClick={closeResolveModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleResolve}
                disabled={resolveLoading || !stripeRefundId.trim()}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark disabled:opacity-50 transition"
              >
                {resolveLoading ? 'Processing...' : 'Confirm Refund'}
              </button>
            </>
          }
        >
          {resolveTarget && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Booking ID</span>
                    <p className="font-mono text-xs mt-1">{resolveTarget.bookingId}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Guide</span>
                    <p className="font-medium mt-1">{resolveTarget.guideName || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Refund Amount</span>
                    <p className="font-medium text-green-600 mt-1">
                      {formatCurrency(resolveTarget.refundAmountCents)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Days Pending</span>
                    <p className="font-medium mt-1">{resolveTarget.daysPending}d</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stripe Refund ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={stripeRefundId}
                  onChange={(e) => setStripeRefundId(e.target.value)}
                  placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the Stripe refund ID from the Stripe dashboard
                </p>
              </div>

              {resolveError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{resolveError}</p>
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    </DashboardLayout>
  );
}
